import React, { useState, useEffect, useCallback } from 'react';
import { FaUpload, FaUserPlus, FaEdit, FaTrash, FaEye, FaSort, FaFilter, FaTimes } from 'react-icons/fa';
import { db, auth } from '../firebase';
import { collection, getDocs, deleteDoc, doc, updateDoc, query, where, orderBy, getDoc, setDoc, limit } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';

const Users = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAdminOrManager, setIsAdminOrManager] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [users, setUsers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sortField, setSortField] = useState('firstName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    search: ''
  });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    email: '',
    phoneNumber: '',
    nationalId: '',
    driversLicense: '',
    driverLevel: '',
    manager: '',
    division: '',
    dateOfBirth: '',
    role: '',
    status: 'active',
    profilePicture: null,
    documents: {
      driversLicense: null,
      passport: null,
      nationalId: null,
      contract: null
    }
  });

  const checkAuthAndRole = useCallback(async () => {
    const user = auth.currentUser;
    
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      // First, try to get user document by document ID (matching auth UID)
      let userDoc = await getDoc(doc(db, 'users', user.uid));
      
      // If document doesn't exist with that ID, try querying by uid field
      if (!userDoc.exists()) {
        const usersRef = collection(db, 'users');
        const uidQuery = query(usersRef, where('uid', '==', user.uid), limit(1));
        const querySnapshot = await getDocs(uidQuery);
        
        if (!querySnapshot.empty) {
          // Found user document with uid field matching auth UID
          userDoc = querySnapshot.docs[0];
        }
      }
      
      if (!userDoc.exists()) {
        navigate('/dashboard');
        return;
      }
      
      const userRole = userDoc.data().role;
      if (userRole !== 'administrator' && userRole !== 'manager') {
        navigate('/dashboard');
        return;
      }
      
      setIsAdminOrManager(true);
      setCurrentUserRole(userRole);
    } catch (err) {
      console.error('Error checking user role:', err);
      navigate('/login');
    }
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      let q = collection(db, 'users');
      
      // Apply filters
      if (filters.role) {
        q = query(q, where('role', '==', filters.role.toLowerCase()));
      }
      if (filters.status) {
        q = query(q, where('status', '==', filters.status.toLowerCase()));
      }
      
      // Apply sorting
      q = query(q, orderBy(sortField, sortDirection));

      const querySnapshot = await getDocs(q);
      let usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        usersList = usersList.filter(user => 
          (user.firstName || '').toLowerCase().includes(searchLower) ||
          (user.lastName || '').toLowerCase().includes(searchLower) ||
          (user.email || '').toLowerCase().includes(searchLower) ||
          (user.nationalId || '').toLowerCase().includes(searchLower)
        );
      }

      setUsers(usersList);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    }
  }, [sortField, sortDirection, filters]);

  useEffect(() => {
    checkAuthAndRole();
  }, [checkAuthAndRole]);

  useEffect(() => {
    if (isAdminOrManager) {
      fetchUsers();
    }
  }, [isAdminOrManager, fetchUsers]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && users.length > 0) {
      const userToEdit = users.find((u) => u.id === editId);
      if (userToEdit) {
        handleEditUser(userToEdit);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, users]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      role: '',
      status: '',
      search: ''
    });
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      ...user,
      driverLevel: user.driverLevel || '',
      manager: user.manager || '',
      division: user.division || '',
      documents: user.documents || {
        driversLicense: null,
        passport: null,
        nationalId: null,
        contract: null
      }
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Prevent managers from changing roles to/from administrator
    if (currentUserRole === 'manager') {
      const originalRole = selectedUser.role?.toLowerCase();
      const newRole = formData.role.toLowerCase();
      
      // Prevent changing role to administrator
      if (newRole === 'administrator' && originalRole !== 'administrator') {
        setError('Managers cannot change user roles to administrator.');
        setLoading(false);
        return;
      }
      
      // Prevent changing administrator's role
      if (originalRole === 'administrator' && newRole !== 'administrator') {
        setError('Managers cannot change administrator roles.');
        setLoading(false);
        return;
      }
    }

    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        nationalId: formData.nationalId,
        driversLicense: formData.driversLicense,
        driverLevel: formData.driverLevel,
        manager: formData.manager,
        division: formData.division,
        dateOfBirth: formData.dateOfBirth,
        role: formData.role.toLowerCase(),
        status: formData.status.toLowerCase(),
        updatedAt: new Date().toISOString()
      };

      // Only update profile picture if a new one is uploaded
      if (formData.profilePicture && formData.profilePicture !== selectedUser.profilePicture) {
        updateData.profilePicture = await handleFileUpload(formData.profilePicture, 'profile_pictures');
      }

      // Update documents if new ones are uploaded
      const documentUrls = { ...selectedUser.documents };
      for (const [docType, file] of Object.entries(formData.documents)) {
        if (file && file !== selectedUser.documents?.[docType]) {
          documentUrls[docType] = await handleFileUpload(file, 'documents');
        }
      }
      updateData.documents = documentUrls;

      await updateDoc(userRef, updateData);
      setSuccess('User updated successfully!');
      setShowEditModal(false);
      fetchUsers();
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      setSuccess('User status updated successfully');
      fetchUsers();
    } catch (err) {
      console.error('Error updating user status:', err);
      setError('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        setSuccess('User deleted successfully');
        fetchUsers();
      } catch (err) {
        console.error('Error deleting user:', err);
        setError('Failed to delete user');
      }
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return null;
    
    try {
      console.log('Starting file upload:', { type, fileName: file.name });
      
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const base64File = await base64Promise;

      console.log('File converted to base64');

      // Upload file using the API
      const response = await fetch('https://novapioneer.andama.ai/api/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file: base64File,
          type,
          fileName: file.name,
        }),
      });

      console.log('Upload response status:', response.status);

      // Try to parse the response as JSON
      let data;
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse response as JSON:', text);
        throw new Error(`Server returned invalid JSON: ${text}`);
      }

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to upload file');
      }

      if (!data.url) {
        throw new Error('No URL returned from upload');
      }

      console.log('File upload successful:', data.url);
      return data.url;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error(`Failed to upload ${type}: ${error.message}`);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Prevent managers from creating administrators
    if (currentUserRole === 'manager' && formData.role.toLowerCase() === 'administrator') {
      setError('Managers cannot create administrator accounts.');
      setLoading(false);
      return;
    }

    try {
      // Upload profile picture
      let profilePictureUrl = null;
      if (formData.profilePicture) {
        profilePictureUrl = await handleFileUpload(formData.profilePicture, 'profile_pictures');
      }

      // Upload documents
      const documentUrls = {};
      for (const [docType, file] of Object.entries(formData.documents)) {
        if (file) {
          try {
            documentUrls[docType] = await handleFileUpload(file, 'documents');
          } catch (error) {
            console.error(`Error uploading ${docType}:`, error);
          }
        }
      }

      // Create user in Firebase Auth
      const authResponse = await fetch('https://novapioneer.andama.ai/api/createUser', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          displayName: `${formData.firstName} ${formData.lastName}`,
        }),
      });

      if (!authResponse.ok) {
        const errorData = await authResponse.json();
        console.error('Create user API error:', {
          error: errorData.error,
          details: errorData.details,
          code: errorData.code
        });
        throw new Error(errorData.details || errorData.error || 'Failed to create user account');
      }

      const authData = await authResponse.json();
      console.log('User created successfully with ID:', authData.uid);

      // Add user to Firestore with document ID matching auth UID
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        nationalId: formData.nationalId,
        driversLicense: formData.driversLicense,
        driverLevel: formData.driverLevel,
        manager: formData.manager,
        division: formData.division,
        dateOfBirth: formData.dateOfBirth,
        role: formData.role.toLowerCase(),
        status: formData.status.toLowerCase(),
        profilePicture: profilePictureUrl,
        documents: documentUrls,
        preferredTwitterUsername: 'Ma3Route',
        createdAt: new Date().toISOString(),
        uid: authData.uid
      };

      // Use setDoc with the auth UID as document ID to ensure it matches
      await setDoc(doc(db, 'users', authData.uid), userData);

      setSuccess('User added successfully! A password reset email has been sent to their email address.');
      setShowAddForm(false);
      setFormData({
        firstName: '',
        lastName: '',
        gender: '',
        email: '',
        phoneNumber: '',
        nationalId: '',
        driversLicense: '',
        driverLevel: '',
        manager: '',
        division: '',
        dateOfBirth: '',
        role: '',
        status: 'active',
        profilePicture: null,
        documents: {
          driversLicense: null,
          passport: null,
          nationalId: null,
          contract: null
        }
      });
      fetchUsers();
    } catch (err) {
      console.error('Error adding user:', {
        message: err.message,
        code: err.code
      });
      setError(err.message || 'Failed to add user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdminOrManager) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Users...</h2>
          <p className="text-gray-500 mt-2">Please wait while we fetch your user data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-black">Users Management</h1>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              <FaFilter />
              <span>Filters</span>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors"
            >
              <FaUserPlus />
              <span>Add New User</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search users..."
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  name="role"
                  value={filters.role}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">All Roles</option>
                  <option value="driver">Driver</option>
                  <option value="bus_assistant">Bus Assistant</option>
                  <option value="manager">Manager</option>
                  <option value="educator">Educator</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                >
                  <FaTimes />
                  <span>Clear Filters</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Users List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('firstName')}>
                  <div className="flex items-center space-x-1">
                    <span>User</span>
                    <FaSort className={`text-gray-400 ${sortField === 'firstName' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('role')}>
                  <div className="flex items-center space-x-1">
                    <span>Role</span>
                    <FaSort className={`text-gray-400 ${sortField === 'role' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('email')}>
                  <div className="flex items-center space-x-1">
                    <span>Email</span>
                    <FaSort className={`text-gray-400 ${sortField === 'email' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')}>
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    <FaSort className={`text-gray-400 ${sortField === 'status' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        {user.profilePicture ? (
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={user.profilePicture}
                            alt={`${user.firstName} ${user.lastName}`}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-500 text-sm">
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.nationalId}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.status || 'active'}
                      onChange={(e) => handleStatusChange(user.id, e.target.value)}
                      className="px-2 py-1 text-xs font-semibold rounded-full focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                      style={{
                        backgroundColor: user.status === 'active' ? '#DEF7EC' : user.status === 'inactive' ? '#FDE8E8' : '#FEF3C7',
                        color: user.status === 'active' ? '#03543F' : user.status === 'inactive' ? '#9B1C1C' : '#92400E'
                      }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleViewUser(user)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit User"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete User"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* View User Modal */}
        {showViewModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">User Details</h2>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-[#00E0E0] mb-4">Personal Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedUser.firstName} {selectedUser.lastName}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Gender</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedUser.gender}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedUser.dateOfBirth}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedUser.phoneNumber}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">National ID</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedUser.nationalId}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-[#00E0E0] mb-4">Contact & Role</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedUser.email}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Role</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedUser.role}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedUser.status || 'Active'}</p>
                      </div>
                      {selectedUser.role === 'driver' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Driver's License</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedUser.driversLicense}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Driver Level</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedUser.driverLevel || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Manager</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedUser.manager || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Division</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedUser.division || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <h3 className="text-lg font-semibold text-[#00E0E0] mb-4">Documents</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedUser.documents && Object.entries(selectedUser.documents).map(([docType, url]) => (
                        url && (
                          <div key={docType}>
                            <label className="block text-sm font-medium text-gray-700 capitalize">
                              {docType.replace(/([A-Z])/g, ' $1').trim()}
                            </label>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                              View Document
                            </a>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">Edit User</h2>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleUpdateUser} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Personal Information */}
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-[#00E0E0]">Personal Information</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
                        <select
                          name="gender"
                          required
                          value={formData.gender}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
                        <input
                          type="date"
                          name="dateOfBirth"
                          required
                          value={formData.dateOfBirth}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          required
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          placeholder="+1234567890"
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>
                    </div>

                    {/* Contact & Role Information */}
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-[#00E0E0]">Contact & Role</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                        <select
                          name="role"
                          required
                          value={formData.role}
                          onChange={handleInputChange}
                          disabled={currentUserRole === 'manager' && selectedUser?.role?.toLowerCase() === 'administrator'}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">Select Role</option>
                          <option value="driver">Driver</option>
                          <option value="bus_assistant">Bus Assistant</option>
                          <option value="manager">Manager</option>
                          <option value="educator">Educator</option>
                          {currentUserRole === 'administrator' && (
                            <option value="administrator">Administrator</option>
                          )}
                        </select>
                        {currentUserRole === 'manager' && selectedUser?.role?.toLowerCase() === 'administrator' && (
                          <p className="mt-1 text-xs text-gray-500">Managers cannot modify administrator roles.</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">National ID/Passport Number *</label>
                        <input
                          type="text"
                          name="nationalId"
                          required
                          value={formData.nationalId}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Driver's License Number {formData.role === 'driver' && '*'}</label>
                        <input
                          type="text"
                          name="driversLicense"
                          required={formData.role === 'driver'}
                          value={formData.driversLicense}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Driver Level</label>
                        <input
                          type="text"
                          name="driverLevel"
                          value={formData.driverLevel}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Manager</label>
                        <input
                          type="text"
                          name="manager"
                          value={formData.manager}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Division</label>
                        <input
                          type="text"
                          name="division"
                          value={formData.division}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Driver Level</label>
                        <input
                          type="text"
                          name="driverLevel"
                          value={formData.driverLevel}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Manager</label>
                        <input
                          type="text"
                          name="manager"
                          value={formData.manager}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Division</label>
                        <input
                          type="text"
                          name="division"
                          value={formData.division}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Documents Upload */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-[#00E0E0]">Documents</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture *</label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="file"
                            accept="image/*"
                            required
                            onChange={(e) => setFormData(prev => ({ ...prev, profilePicture: e.target.files[0] }))}
                            className="hidden"
                            id="profilePicture"
                          />
                          <label
                            htmlFor="profilePicture"
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#313643] text-white cursor-pointer hover:bg-[#3a3f4e] transition-colors"
                          >
                            <FaUpload />
                            <span>Upload Photo</span>
                          </label>
                          {formData.profilePicture && (
                            <span className="text-sm text-gray-500">{formData.profilePicture.name}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Driver's License (PDF)</label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              documents: { ...prev.documents, driversLicense: e.target.files[0] }
                            }))}
                            className="hidden"
                            id="driversLicense"
                          />
                          <label
                            htmlFor="driversLicense"
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#313643] text-white cursor-pointer hover:bg-[#3a3f4e] transition-colors"
                          >
                            <FaUpload />
                            <span>Upload PDF</span>
                          </label>
                          {formData.documents.driversLicense && (
                            <span className="text-sm text-gray-500">{formData.documents.driversLicense.name}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Passport (PDF)</label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              documents: { ...prev.documents, passport: e.target.files[0] }
                            }))}
                            className="hidden"
                            id="passport"
                          />
                          <label
                            htmlFor="passport"
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#313643] text-white cursor-pointer hover:bg-[#3a3f4e] transition-colors"
                          >
                            <FaUpload />
                            <span>Upload PDF</span>
                          </label>
                          {formData.documents.passport && (
                            <span className="text-sm text-gray-500">{formData.documents.passport.name}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">National ID (PDF)</label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              documents: { ...prev.documents, nationalId: e.target.files[0] }
                            }))}
                            className="hidden"
                            id="nationalId"
                          />
                          <label
                            htmlFor="nationalId"
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#313643] text-white cursor-pointer hover:bg-[#3a3f4e] transition-colors"
                          >
                            <FaUpload />
                            <span>Upload PDF</span>
                          </label>
                          {formData.documents.nationalId && (
                            <span className="text-sm text-gray-500">{formData.documents.nationalId.name}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Contract (PDF)</label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              documents: { ...prev.documents, contract: e.target.files[0] }
                            }))}
                            className="hidden"
                            id="contract"
                          />
                          <label
                            htmlFor="contract"
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#313643] text-white cursor-pointer hover:bg-[#3a3f4e] transition-colors"
                          >
                            <FaUpload />
                            <span>Upload PDF</span>
                          </label>
                          {formData.documents.contract && (
                            <span className="text-sm text-gray-500">{formData.documents.contract.name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`flex items-center space-x-2 px-6 py-3 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <FaEdit />
                      <span>{loading ? 'Updating...' : 'Update User'}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Add User Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">Add New User</h2>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Personal Information */}
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-[#00E0E0]">Personal Information</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
                        <select
                          name="gender"
                          required
                          value={formData.gender}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
                        <input
                          type="date"
                          name="dateOfBirth"
                          required
                          value={formData.dateOfBirth}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          required
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          placeholder="+1234567890"
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>
                    </div>

                    {/* Contact & Role Information */}
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-[#00E0E0]">Contact & Role</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                        <select
                          name="role"
                          required
                          value={formData.role}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        >
                          <option value="">Select Role</option>
                          <option value="driver">Driver</option>
                          <option value="bus_assistant">Bus Assistant</option>
                          <option value="manager">Manager</option>
                          <option value="educator">Educator</option>
                          {currentUserRole === 'administrator' && (
                            <option value="administrator">Administrator</option>
                          )}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">National ID/Passport Number *</label>
                        <input
                          type="text"
                          name="nationalId"
                          required
                          value={formData.nationalId}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Driver's License Number {formData.role === 'driver' && '*'}</label>
                        <input
                          type="text"
                          name="driversLicense"
                          required={formData.role === 'driver'}
                          value={formData.driversLicense}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Documents Upload */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-[#00E0E0]">Documents</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture *</label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="file"
                            accept="image/*"
                            required
                            onChange={(e) => setFormData(prev => ({ ...prev, profilePicture: e.target.files[0] }))}
                            className="hidden"
                            id="profilePicture"
                          />
                          <label
                            htmlFor="profilePicture"
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#313643] text-white cursor-pointer hover:bg-[#3a3f4e] transition-colors"
                          >
                            <FaUpload />
                            <span>Upload Photo</span>
                          </label>
                          {formData.profilePicture && (
                            <span className="text-sm text-gray-500">{formData.profilePicture.name}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Driver's License (PDF)</label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              documents: { ...prev.documents, driversLicense: e.target.files[0] }
                            }))}
                            className="hidden"
                            id="driversLicense"
                          />
                          <label
                            htmlFor="driversLicense"
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#313643] text-white cursor-pointer hover:bg-[#3a3f4e] transition-colors"
                          >
                            <FaUpload />
                            <span>Upload PDF</span>
                          </label>
                          {formData.documents.driversLicense && (
                            <span className="text-sm text-gray-500">{formData.documents.driversLicense.name}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Passport (PDF)</label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              documents: { ...prev.documents, passport: e.target.files[0] }
                            }))}
                            className="hidden"
                            id="passport"
                          />
                          <label
                            htmlFor="passport"
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#313643] text-white cursor-pointer hover:bg-[#3a3f4e] transition-colors"
                          >
                            <FaUpload />
                            <span>Upload PDF</span>
                          </label>
                          {formData.documents.passport && (
                            <span className="text-sm text-gray-500">{formData.documents.passport.name}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">National ID (PDF)</label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              documents: { ...prev.documents, nationalId: e.target.files[0] }
                            }))}
                            className="hidden"
                            id="nationalId"
                          />
                          <label
                            htmlFor="nationalId"
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#313643] text-white cursor-pointer hover:bg-[#3a3f4e] transition-colors"
                          >
                            <FaUpload />
                            <span>Upload PDF</span>
                          </label>
                          {formData.documents.nationalId && (
                            <span className="text-sm text-gray-500">{formData.documents.nationalId.name}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Contract (PDF)</label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              documents: { ...prev.documents, contract: e.target.files[0] }
                            }))}
                            className="hidden"
                            id="contract"
                          />
                          <label
                            htmlFor="contract"
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#313643] text-white cursor-pointer hover:bg-[#3a3f4e] transition-colors"
                          >
                            <FaUpload />
                            <span>Upload PDF</span>
                          </label>
                          {formData.documents.contract && (
                            <span className="text-sm text-gray-500">{formData.documents.contract.name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`flex items-center space-x-2 px-6 py-3 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <FaUserPlus />
                      <span>{loading ? 'Adding User...' : 'Add User'}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users; 